import { create } from 'zustand';
import { customersApi } from '../api';

const useCustomerStore = create((set, get) => ({
  customers: [],
  currentCustomer: null,
  tags: [],
  totalCount: 0,
  isLoading: false,
  error: null,

  setCustomers: (customers) => set({ customers }),

  setCurrentCustomer: (customer) => set({ currentCustomer: customer }),

  fetchCustomers: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.getAll(params);
      set({
        customers: response.data.data || response.data,
        totalCount: response.data.total || response.data.length,
        isLoading: false
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch customers', isLoading: false });
      throw error;
    }
  },

  fetchCustomerById: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.getById(id);
      set({ currentCustomer: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch customer', isLoading: false });
      throw error;
    }
  },

  createCustomer: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.create(data);
      set((state) => ({
        customers: [...state.customers, response.data],
        totalCount: state.totalCount + 1,
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to create customer', isLoading: false });
      throw error;
    }
  },

  updateCustomer: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.update(id, data);
      set((state) => ({
        customers: state.customers.map(c => c.id === id ? response.data : c),
        currentCustomer: state.currentCustomer?.id === id ? response.data : state.currentCustomer,
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to update customer', isLoading: false });
      throw error;
    }
  },

  deleteCustomer: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await customersApi.delete(id);
      set((state) => ({
        customers: state.customers.filter(c => c.id !== id),
        currentCustomer: state.currentCustomer?.id === id ? null : state.currentCustomer,
        totalCount: state.totalCount - 1,
        isLoading: false
      }));
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to delete customer', isLoading: false });
      throw error;
    }
  },

  importCustomers: async (formData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.import(formData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to import customers', isLoading: false });
      throw error;
    }
  },

  blockCustomer: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.block(id);
      set((state) => ({
        customers: state.customers.map(c => c.id === id ? { ...c, blocked: true } : c),
        currentCustomer: state.currentCustomer?.id === id ? { ...state.currentCustomer, blocked: true } : state.currentCustomer,
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to block customer', isLoading: false });
      throw error;
    }
  },

  unblockCustomer: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await customersApi.unblock(id);
      set((state) => ({
        customers: state.customers.map(c => c.id === id ? { ...c, blocked: false } : c),
        currentCustomer: state.currentCustomer?.id === id ? { ...state.currentCustomer, blocked: false } : state.currentCustomer,
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to unblock customer', isLoading: false });
      throw error;
    }
  },

  fetchTags: async () => {
    try {
      const response = await customersApi.getTags();
      set({ tags: response.data });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({ customers: [], currentCustomer: null, tags: [], totalCount: 0, isLoading: false, error: null }),
}));

export default useCustomerStore;
